import { Component, DestroyRef, inject, signal, OnInit, computed, effect } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { map, distinctUntilChanged, switchMap, filter, tap, of } from 'rxjs';
import { ToggleSwitch } from '@openng/optimus-ui/toggleswitch';
import { DropdownData, DynamicRouteHandlingService, InputFieldComponent, LookupsService } from '@zayan-erp/shared';
import { GenericDropdownComponent } from '@zayan-erp/shared';
import { MajorCodeService } from '../../services/major-code.service';
import { JobTypeService } from '../../services/job-type.service';
import { MajorCodeType } from '../../enums/major-codes.enum';
import { MajorCode } from '../../models/major-code.model';
@Component({
  selector: 'major-code-form',
  imports: [ReactiveFormsModule, GenericDropdownComponent, InputFieldComponent, ToggleSwitch],
  templateUrl: './major-code-form.component.html',
  styles: [``]
})
export class MajorCodeFormComponent implements OnInit {
  // Injected services
  majorCodeService = inject(MajorCodeService);
  jobTypeService = inject(JobTypeService);
  activatedRoute = inject(ActivatedRoute);
  lookupsService = inject(LookupsService);
  #router = inject(Router);
  dynamicRouteHandling = inject(DynamicRouteHandlingService);
  destroyRef = inject(DestroyRef);

  // State signals
  headerMajorCodeOptions = signal<DropdownData[]>([]);
  originalTypeIsHeader = signal<boolean>(false);

  // Dropdown data
  jobTypeDropdownItems = toSignal(
    this.jobTypeService
      .readJobTypes()
      .pipe(
        map(res =>
          res.map(item => ({ id: item.id, firstLabel: item.code, secondLabel: item.abbreviation }) as DropdownData)
        )
      ),
    { initialValue: [] }
  );
  uofmList = toSignal(
    this.lookupsService
      .readUofm()
      .pipe(
        map(res =>
          res.map(item => ({ id: item.id, firstLabel: item.description, secondLabel: item.code }) as DropdownData)
        )
      ),
    { initialValue: [] }
  );
  private typeDropdownItems = toSignal(this.majorCodeService.getMajorCodeStatusList(), { initialValue: [] });
  jobTypeQueryParam = toSignal(this.activatedRoute.queryParamMap.pipe(map(params => params.get('jobTypeId'))), {
    initialValue: null
  });

  selectedMajorCodeId = signal<string | null>(this.activatedRoute.snapshot.paramMap.get('id'));

  recordToEditRxResource = rxResourceFixed({
    params: () => ({ id: this.selectedMajorCodeId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (params.id) {
        return this.majorCodeService.readMajorCodeById(params.id).pipe(
          tap(majorCode => {
            this.onDataLoaded(majorCode);
          })
        );
      }
      return of(null);
    }
  });

  recordToEditType = computed(() => this.recordToEditRxResource.value()?.type);

  onIdToEditChange = effect(() => {
    // In case of edit action, disable job type dropdown
    if (this.selectedMajorCodeId()) {
      this.majorCodeForm.controls.jobTypeId.disable();
    } else {
      this.majorCodeForm.controls.jobTypeId.enable();
    }
  });

  typeDropdownItemsFiltered = computed(() => {
    let options: DropdownData[] = [...this.typeDropdownItems()];

    // If no record to edit, return all options
    if (!this.recordToEditRxResource.value()) return options;

    // If record to edit is header, return only header as option
    if (this.recordToEditType() === MajorCodeType.Header) {
      options = options.filter(o => String(o.id) === String(MajorCodeType.Header));
    }

    // If record to edit is not header, return all options except header
    if (this.recordToEditType() !== MajorCodeType.Header) {
      options = options.filter(o => String(o.id) !== String(MajorCodeType.Header));
    }

    return options;
  });

  editConditionsSet = effect(() => {
    if (this.recordToEditRxResource.value()) {
      if (this.recordToEditType() === MajorCodeType.Header) {
        this.majorCodeForm.controls.type.disable();
      } else {
        this.majorCodeForm.controls.type.enable();
      }
    }
  });

  // Form definition
  majorCodeForm = new FormGroup({
    jobTypeId: new FormControl<string | undefined>(undefined, Validators.required),
    code: new FormControl<string | null>(null, Validators.required),
    description: new FormControl<string | null>(null, Validators.required),
    type: new FormControl<string | null>(null, Validators.required),
    headerMajorCodeId: new FormControl<string | null>(null),
    unitOfMeasureId: new FormControl<string | null>(null),
    productionRequired: new FormControl<boolean>(false),
    isActive: new FormControl<boolean>(true)
  });

  onJobTypeQueryParamChange = effect(() => {
    this.majorCodeForm.patchValue({
      jobTypeId: this.jobTypeQueryParam()
    });
  });

  ngOnInit(): void {
    // Handle form submission from external button
    this.dynamicRouteHandling.submitButton$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onSubmit());

    // Setup form control listeners
    this.setupJobTypeChangeListener();
    this.setupTypeChangeListener();
  }

  // Form control listeners
  private setupJobTypeChangeListener(): void {
    this.majorCodeForm.controls.jobTypeId.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
        filter((val): val is string => !!val),
        switchMap(jobTypeId => this.majorCodeService.readMajorCodes(jobTypeId)),
        map(list => list.filter(mc => mc.type === MajorCodeType.Header)),
        map(list =>
          list.map(mc => ({ id: mc.id, firstLabel: mc.code, secondLabel: mc.description ?? '' }) as DropdownData)
        )
      )
      .subscribe(options => {
        this.headerMajorCodeOptions.set(options);
      });
  }

  private setupTypeChangeListener(): void {
    this.majorCodeForm.controls.type.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(val => {
      const isHeader = String(val ?? '') === String(MajorCodeType.Header);
      const headerCtrl = this.majorCodeForm.controls.headerMajorCodeId;
      const uomCtrl = this.majorCodeForm.controls.unitOfMeasureId;
      const prodCtrl = this.majorCodeForm.controls.productionRequired;
      const activeCtrl = this.majorCodeForm.controls.isActive;

      if (isHeader) {
        headerCtrl.setValue(null, { emitEvent: false });
        uomCtrl.setValue(null, { emitEvent: false });
        prodCtrl.setValue(false, { emitEvent: false });
        activeCtrl.setValue(true, { emitEvent: false });
        headerCtrl.disable({ emitEvent: false });
        uomCtrl.disable({ emitEvent: false });
        prodCtrl.disable({ emitEvent: false });
        activeCtrl.disable({ emitEvent: false });
      } else {
        headerCtrl.enable({ emitEvent: false });
        uomCtrl.enable({ emitEvent: false });
        prodCtrl.enable({ emitEvent: false });
        activeCtrl.enable({ emitEvent: false });
      }
    });
  }

  onDataLoaded(majorCode: MajorCode): void {
    this.majorCodeForm.patchValue({
      jobTypeId: majorCode.jobTypeId,
      code: majorCode.code,
      description: majorCode.description,
      type: majorCode.type?.toString(),
      headerMajorCodeId: majorCode.headerMajorCodeId,
      unitOfMeasureId: majorCode.unitOfMeasureId,
      productionRequired: majorCode.productionRequired
    });
  }

  // Form submission
  onSubmit(): void {
    if (this.majorCodeForm.invalid) {
      this.majorCodeForm.markAllAsTouched();
      return;
    }
    const formValue = this.majorCodeForm.value;
    const majorCodeData: Omit<MajorCode, 'id'> = {
      jobTypeId: formValue.jobTypeId!,
      code: formValue.code!,
      description: formValue.description || '',
      type: parseInt(formValue.type || '0'),
      headerMajorCodeId: formValue.headerMajorCodeId ?? null,
      unitOfMeasureId: formValue.unitOfMeasureId ?? null,
      productionRequired: formValue.productionRequired || false,
      isActive: formValue.isActive ?? true
    };

    const operation = this.selectedMajorCodeId()
      ? this.majorCodeService.updateMajorCode({ ...majorCodeData }, this.selectedMajorCodeId()!)
      : this.majorCodeService.createMajorCode(majorCodeData);

    operation.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.dynamicRouteHandling.reload.next(!this.dynamicRouteHandling.reload.value);
        this.#router.navigate([this.activatedRoute.snapshot.data['returnRoute']], { queryParamsHandling: 'preserve' });
      }
    });
  }

  // Helper methods
  isHeaderSelected(): boolean {
    const typeVal = this.majorCodeForm.controls.type.value;
    return String(typeVal ?? '') === String(MajorCodeType.Header);
  }
}
